import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { StorageService } from '../../../services/storage.service';
import { RecentSearchEntry } from '../../../models/search.model';

const RECENT_SEARCHES_KEY = 'recent_searches';
const MAX_RECENT_SEARCHES = 6;

@Component({
  selector: 'app-search-bar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchBarComponent implements OnInit, OnDestroy {
  @Input() placeholder = 'Search products, shops, services...';
  @Input() autofocus = false;
  @Input() showSuggestionsPanel = true;
  /** Set by the parent when it has projected live (typeahead) results to show via [liveResults] content */
  @Input() hasLiveResults = false;
  /** Popular terms supplied by the parent from the backend; the section is hidden when empty. */
  @Input() popularSearches: string[] = [];

  @Input()
  set value(v: string) {
    this.query = v || '';
  }
  get value(): string {
    return this.query;
  }

  /** Debounced (300ms) live-typing emission, for typeahead/suggestions */
  @Output() queryChange = new EventEmitter<string>();
  /** Immediate emission on Enter / search button / suggestion click */
  @Output() search = new EventEmitter<string>();
  @Output() cleared = new EventEmitter<void>();

  query = '';
  showPanel = false;
  recentSearches: RecentSearchEntry[] = [];

  private querySubject = new Subject<string>();
  private sub = new Subscription();

  constructor(private storage: StorageService, private elRef: ElementRef<HTMLElement>) { }

  ngOnInit(): void {
    this.recentSearches = this.storage.getItemA(RECENT_SEARCHES_KEY) || [];
    this.sub.add(
      this.querySubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe(q => {
        this.queryChange.emit(q);
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onInput(value: string): void {
    this.query = value;
    this.querySubject.next(value.trim());
  }

  onFocus(): void {
    this.showPanel = this.showSuggestionsPanel;
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    this.showPanel = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elRef.nativeElement.contains(event.target as Node)) {
      this.showPanel = false;
    }
  }

  submit(): void {
    const q = this.query.trim();
    if (!q) return;
    this.saveRecentSearch(q);
    this.showPanel = false;
    this.search.emit(q);
  }

  clear(): void {
    this.query = '';
    this.showPanel = false;
    this.cleared.emit();
  }

  selectChip(q: string): void {
    this.query = q;
    this.submit();
  }

  removeRecent(query: string, event: Event): void {
    event.stopPropagation();
    this.recentSearches = this.recentSearches.filter(r => r.query !== query);
    this.storage.setItemA(RECENT_SEARCHES_KEY, this.recentSearches);
  }

  clearAllRecent(event: Event): void {
    event.stopPropagation();
    this.recentSearches = [];
    this.storage.setItemA(RECENT_SEARCHES_KEY, []);
  }

  private saveRecentSearch(query: string): void {
    const existing = this.recentSearches.filter(r => r.query.toLowerCase() !== query.toLowerCase());
    this.recentSearches = [{ query, timestamp: Date.now() }, ...existing].slice(0, MAX_RECENT_SEARCHES);
    this.storage.setItemA(RECENT_SEARCHES_KEY, this.recentSearches);
  }
}
